import {
	Component, OnInit, OnDestroy, AfterViewInit, OnChanges, SimpleChanges, SimpleChange,
	ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, Input, Output, EventEmitter, Inject,
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { IpAddress, MacAddress, SnmpOid } from 'DiscoveryEngine.TS/Core';
import { DiscResultsService, AddressType } from 'discovery';
import { IdentityService } from 'identity';
import { NavigationService } from 'netview';
import { MessageService } from 'base';
import {
	DisplayDeviceListDto, DisplayDeviceListEntryDto, DisplayDeviceGroupDto, DeviceDto,
	InfoSetView, InfoSetViewColumn, InfoSetViewEntry, InfoSetViewValue
} from 'UvExplorerServer.Dtos.TS/Session';
import { ToolsService } from 'tools';
import { DeviceListEntry } from './device-list-entry.model'; 
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { DeviceDetailsComponent, InfoSetsComponent } from 'infosets';
import { MenuItem } from 'primeng/api';
//import { Table } from 'primeng/table';
import { UvTable } from 'widget';
import { DOCUMENT } from '@angular/common';


@Component({
	selector: 'device-list',
	templateUrl: './devicelist.component.html',
	styleUrls: ['./devicelist.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceListComponent implements OnInit, OnDestroy, OnChanges {

	public readonly UseOffCanvas: boolean = false;

	@Input()
	public loading: boolean;

	@Output()
	public onShowDeviceOnMap: EventEmitter<string>;

    @ViewChild("devicedetails", { static: true })
	public deviceDetails: DeviceDetailsComponent;

	@ViewChild("infosets", { static: false })
	public infoSets: InfoSetsComponent;

	@ViewChild('detailsOffCanvas') 
	public detailsOffCanvas: any;

	@ViewChild('dt')
	//public table: Table;
	public table: UvTable;

	public cmItems: MenuItem[];

	public selectedDeviceGroup: DisplayDeviceGroupDto;
	public get hasSelectedDeviceGroup(): boolean {
		return !!this.selectedDeviceGroup;
	}

	public setSelectedDeviceGroup(ddg: DisplayDeviceGroupDto): void {
		this.selectedDeviceGroup = ddg;
		this.rebuildEntries();
		this.selectedEntry = null;
		this.selectedDevice = null;
	}

	private deviceList: DisplayDeviceListDto;
	private deviceListLoadedSub: Subscription;

	public cols: any[];
	public entries: DeviceListEntry[];
	public first: number;

	public selectedEntry: DeviceListEntry;
	public get hasSelectedEntry(): boolean {
		return !!this.selectedEntry;
	}

	public selectedDevice: DeviceDto;
	public get hasSelectedDevice(): boolean {
		return !!this.selectedDevice;
	}

	public ipMacSightings: InfoSetView;
	public ipMacFinderResultDialogVisible: boolean;
	private ipFilterValue: string;
	private macFilterValue: string;

	private devicesChangedSub: Subscription;

	public cmSelectedDevice: DeviceListEntry;

	public readonly VirtualScrollItemSize = 29;
	public get TableRowStyle(): string {
		return `height: ${this.VirtualScrollItemSize}px;`;
	}

	private resizeObserver: ResizeObserver;

	constructor(private changeDetector: ChangeDetectorRef,
		private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private messageService: MessageService,
		private toolsService: ToolsService,
		@Inject(DOCUMENT) private document: Document) {

		this.cols = [
			{ field: 'name', header: 'Name', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ipAddress', header: 'IP Address', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'macAddress', header: 'MAC Address', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'description', header: 'Description', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'snmpOid', header: 'SNMP OID', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'vendor', header: 'Vendor', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'notes', header: 'Notes', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
		];
		this.entries = [];
		this.first = 0;
		this.selectedEntry = null;
		this.selectedDevice = null;

		this.deviceList = this.emptyDeviceList();

		this.deviceListLoadedSub = discResultsService.onDeviceListLoaded.subscribe(
			(deviceList: DisplayDeviceListDto) => {
				this.setDeviceList(deviceList);
			},
			(error?) => {
				this.setDeviceList(null);
			});

		this.ipMacSightings = this.emptySightingsList();

		this.ipMacFinderResultDialogVisible = false;

		this.devicesChangedSub = toolsService.devicesChanged.subscribe((changedDeviceGuids: string[]) => {
			if (this.hasSelectedEntry && this.selectedEntry.hasDeviceGuid) {
				if (changedDeviceGuids.includes(this.selectedEntry.deviceGuid)) {

					let sessionGuid = this.identityService.sessionGuid;
					let netViewGuid = this.navService.location.netViewGuid;
					let agentGuid = this.navService.location.agentGuid;
					let schedDiscGuid = this.navService.location.schedDiscGuid;
					let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;
					let deviceGuid = this.selectedEntry.deviceGuid;
		
					this.discResultsService.getDeviceView(sessionGuid, netViewGuid, agentGuid,
						schedDiscGuid, schedDiscRsltGuid, deviceGuid).subscribe(
						(result: DeviceDto) => {
							if (this.UseOffCanvas) {
								// Infosets are undefined here :shrug:
								this.infoSets.patchData(result);
							}
							else {
								this.deviceDetails.patchData(result);
							}

							this.changeDetector.markForCheck();
						},
						(error?) => {
							this.messageService.displayErrorMessage("Could not retrieve the selected device");
						});
				}
			}
		});

		this.loading = false;
		this.cmItems = this.getContextMenuItems();
		this.onShowDeviceOnMap = new EventEmitter<string>();
	}

	private connectResizeObserver(): void {
		let bodyElem = <HTMLBodyElement>this.document.querySelector("body");
		this.resizeObserver = new ResizeObserver((entries) => this.onResize())
		this.resizeObserver.observe(bodyElem);
	}

	private disconnectResizeObserver(): void {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	}

	private setTableScrollerHeight(): void {
		if (!!this.table && !!this.table.scroller && !!this.table.scroller.elementViewChild) {
			this.table.scroller.elementViewChild.nativeElement.style.height = "calc(100vh - 300px)";
		}
	}

	public onResize(): void {
		this.setTableScrollerHeight();
	}

	protected getContextMenuItems(): MenuItem[] {
		return [
			{ label: "Show on Map", command: (event) => this.showDeviceOnMap(this.cmSelectedDevice) },
		];
	}

	protected getAdminContextMenuItems(): MenuItem[] {
		return [
			{ label: "Show on Map", command: (event) => this.showDeviceOnMap(this.cmSelectedDevice) },
			{ separator: true },
			{ label: "Delete Device", command: (event) => this.deleteDevice(this.cmSelectedDevice) }
		];
	}

	private showDeviceOnMap(device: DeviceListEntry): void {
		console.log(device);
		this.onShowDeviceOnMap.emit(device.deviceGuid);
	}

	private deleteDevice(device: DeviceListEntry): void {
		console.log(device);

		if (this.identityService.isAdmin) {
			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;

			this.discResultsService.deleteMasterDevice(sessionGuid, netViewGuid, device.deviceGuid).subscribe(
				(result: boolean) => {
					this.reloadDeviceList();
				},
				(error: any) => {
					this.messageService.displayErrorMessage('Could not delete the device');
				});
		}
	}


	private emptyDeviceList(): DisplayDeviceListDto {
		return {
			entries: []
		};
	}

	public reloadDeviceList(): void {
		setTimeout(() => { 
			this.entries = [...this.entries];
			this.changeDetector.markForCheck();
		}, 0);
	}

	private setDeviceList(deviceList: DisplayDeviceListDto): void {
		if (deviceList) {
			this.deviceList = deviceList;
		}
		else {
			this.deviceList = this.emptyDeviceList();
		}

		this.rebuildEntries();
	}

	private emptySightingsList(): InfoSetView {
		return {
			infoSetName: "",
			title: "",
			columns: [],
			entries: []
		};
	}

	public ngOnInit(): void {
		this.rebuildEntries();
		this.connectResizeObserver();
	}

	public ngOnDestroy(): void {
		if (this.deviceListLoadedSub) {
			this.deviceListLoadedSub.unsubscribe();
			this.deviceListLoadedSub = undefined;
		}
		if (this.devicesChangedSub) {
			this.devicesChangedSub.unsubscribe();
			this.devicesChangedSub = undefined;
		}

		this.disconnectResizeObserver();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.setTableScrollerHeight();
	}

	public setVisible(value: boolean): void {
		if (value) {
			this.setTableScrollerHeight();
		}
	}

	private rebuildEntries(): void {
		this.changeDetector.markForCheck();

		// Preserve selected device if possible
		let selectedDataKey = (this.hasSelectedEntry ? this.selectedEntry.dataKey : "");

		this.entries = [];
		this.first = 0;
		this.selectedEntry = null;

		this.deviceList.entries.forEach((dle: DisplayDeviceListEntryDto, index: number) => {

			let entry = new DeviceListEntry();

			entry.name = dle.name;

			if (dle.ipAddress) {
				entry.ipAddress = dle.ipAddress;
				entry.ipAddressTag = IpAddress.Create(dle.ipAddress);
			}
			if (dle.macAddress) {
				entry.macAddress = dle.macAddress;
				entry.macAddressTag = MacAddress.CreateFromString(dle.macAddress);
			}
			if (dle.systemDescription) {
				entry.description = dle.systemDescription;
			}
			if (dle.systemOID) {
				entry.snmpOid = dle.systemOID;
				entry.snmpOidTag = SnmpOid.Create(dle.systemOID);
			}
			if (dle.vendor) {
				entry.vendor = dle.vendor;
			}
			if (dle.notes) {
				entry.notes = dle.notes;
			}
			if (dle.deviceGuid) {
				entry.deviceGuid = dle.deviceGuid;
				entry.dataKey = dle.deviceGuid;
			}

			if (entry.dataKey === selectedDataKey) {
				this.selectedEntry = entry;
			}

			this.entries.push(entry);

		}, this);
		// TODO - FINISH THIS METHOD
		//this.changeDetector.detectChanges();
	}

	public doCustomSort(event): void {
		let comparer = function (a: DeviceListEntry, b: DeviceListEntry): number {
			let result = 0;

			switch (event.field) {
				case "ipAddress":
					if (a.ipAddressTag && b.ipAddressTag) {
						result = a.ipAddressTag.CompareTo(b.ipAddressTag);
					}
					else if (a.ipAddressTag) {
						result = 1;
					}
					else if (b.ipAddressTag) {
						result = -1;
					}
					break;
				case "macAddress":
					if (a.macAddressTag && b.macAddressTag) {
						result = a.macAddressTag.CompareTo(b.macAddressTag);
					}
					else if (a.macAddressTag) {
						result = 1;
					}
					else if (b.macAddressTag) {
						result = -1;
					}
					break;
				case "snmpOid":
					if (a.snmpOidTag && b.snmpOidTag) {
						result = a.snmpOidTag.CompareTo(b.snmpOidTag);
					}
					else if (a.snmpOidTag) {
						result = 1;
					}
					else if (b.snmpOidTag) {
						result = -1;
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].localeCompare(b[event.field]);
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

	public clearSelection(): void {
		this.selectedEntry = null;
		this.selectedDevice = null;
	}

	public onRowSelect(event): void {
		this.deriveSelectedDevice();
	}

	public onRowUnselect(event): void {
		this.deriveSelectedDevice();
	}

	public onFilter(event): void {
		let ipAddressFilter = event.filters?.ipAddress;
		
		if (!!ipAddressFilter && this.isValidIpAddress(ipAddressFilter.value)) {
			if (this.ipFilterValue !== ipAddressFilter.value) {
				this.ipFilterValue = ipAddressFilter.value;
            }
		} else {
			if (!!this.ipFilterValue) {
				this.ipFilterValue = null;
			}
		}

		let macAddressFilter = event.filters?.macAddress;

		if (!!macAddressFilter && this.isValidMacAddress(macAddressFilter.value)) {
			if (this.macFilterValue !== macAddressFilter.value) {
				this.macFilterValue = macAddressFilter.value;
			}
		} else {
			if (!!this.macFilterValue) {
				this.macFilterValue = null;
			}
        }
	}

	private isValidIpAddress(value: string): boolean {
		let regex = /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])$/
		return regex.test(value);
    }

	private isValidMacAddress(value: string): boolean {
		let regex = /^[0-9A-Fa-f]{2}([:\-])(?:[0-9A-Fa-f]{2}\1){4}[0-9A-Fa-f]{2}$/;
		return regex.test(value);
	}

	private deriveSelectedDevice(): void {
		if (this.selectedEntry && this.selectedEntry.hasDeviceGuid) {

			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;
			let deviceGuid = this.selectedEntry.deviceGuid;

			this.discResultsService.getDeviceView(sessionGuid, netViewGuid, agentGuid,
				schedDiscGuid, schedDiscRsltGuid, deviceGuid).subscribe(
				(result: DeviceDto) => {				
					this.selectedDevice = result;

					if (this.UseOffCanvas) {
						this.detailsOffCanvas.open();
					}
					else {
						this.deviceDetails.show();
					}
					
					this.changeDetector.markForCheck();
				},
				(error?) => {
					this.messageService.displayErrorMessage("Could not retrieve the selected device");
				});
		}
		else {
			this.selectedDevice = null;			
		}
	}

	// Make the enum available to the html template
	AddressType = AddressType;

	public findIpMac(addressType: AddressType): void {
		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		if (!!sessionGuid && !!netViewGuid && (!!this.selectedEntry || !!this.ipFilterValue || this.macFilterValue)) {
			let ipAddress = this.selectedEntry?.ipAddress || this.ipFilterValue;
			let macAddress = this.selectedEntry?.macAddress || this.macFilterValue;

			if ((addressType === AddressType.Ip && !!ipAddress) || (addressType === AddressType.Mac && !!macAddress)) {
				this.discResultsService.getIpMacSightings(sessionGuid, netViewGuid, agentGuid,
					schedDiscGuid, schedDiscRsltGuid, ipAddress, macAddress, addressType).subscribe(
						(result: InfoSetView) => {
							if (result.entries.length > 0) {
								this.ipMacSightings = result;
								this.ipMacFinderResultDialogVisible = true;
								this.changeDetector.markForCheck();
							} else {
								this.messageService.displayInfoMessage(`No sightings found for ${addressType === AddressType.Ip ? ipAddress : macAddress}`);
                            }
						},
						(error?) => {
							this.messageService.displayErrorMessage(`Could not retrieve sightings for ${addressType === AddressType.Ip ? ipAddress : macAddress}`);
						});
            }
		}
	}

}
